import { GetStaticProps } from 'next';
import { useEffect, useState } from 'react';

import { ALL_MENU_QUERY, MenuLocationEnum } from '@/data/allMenuQuery';

import { flatListToHierarchical } from '@/utils/flatListToHierarchical';
import { getApolloClient } from '@/utils/apollo-client';
import { transformMenus } from '@/utils/transformMenus';
import { withLayout } from '@/layout/Layout';

import { LayoutProps } from '@/layout/Layout/Layout.props';
import { PageProps } from '@/types/types';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

import { removeLastTrailingSlash } from '@/utils/removeLastTrailingSlash';

import { NEWS_PAGE_QUERY } from '@/data/newsPageQuery';
import { Section } from '@/components/common/Section';
import { Heading } from '@/components/typography';
import { CommonCard } from '@/components/cards/CommonCard';
import { Pagination } from '@/components/navigation/Pagination';
import { useTranslation } from 'next-i18next';
import {
  FooterMenuLocationEnum,
  FOOTER_DATA_QUERY,
  FOOTER_DATA_QUERY_EN,
} from '@/data/footerDataQuery';
import { footerDataTransformer } from '@/utils/footerDataTransformer';
import { useRouter } from 'next/router';
import { paginate } from '@/utils/paginate';
import { PageHead } from '@/components/common/PageHead';

// LABEL: Page component
const Page = ({ data }: PageProps & LayoutProps) => {
  const [isMounted, setIsMounted] = useState(false);
  const { t } = useTranslation('common');
  const { title, seo } = data?.pageBy?.translation ?? {};
  const newsList = data?.allNews?.nodes ?? {};
  const btnText = t('buttonMore');

  // LABEL: Pagination necessary variables
  const total = data?.allNews?.pageInfo?.offsetPagination?.total || 0;
  const router = useRouter();
  const queryPage = Number(router?.query?.page) || 1;
  const PAGE_SIZE = 9;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  return (
    <>
      <PageHead
        title={seo?.title || title}
        seo={seo || {}}
        canonicalUrl={seo?.canonicalUrl}
        breadcrumbs={seo?.breadcrumbs}
      />
      {isMounted && (
        <>
          <Section breadcrumbs={seo?.breadcrumbs} lazy={false}>
            {title && (
              <Heading
                tag="h1"
                withDivider
                content={title}
                className="mb-20 md:mb-25"
              />
            )}
            {newsList && (
              <ul className="grid gap-8 md:grid md:grid-cols-2 lg:grid-cols-3">
                {paginate(newsList, queryPage, PAGE_SIZE).map(item => {
                  const { title, id, uri, date, excerpt, featuredImage } = item;

                  return (
                    <li key={id} className="md:h-full">
                      <CommonCard
                        commonImage={featuredImage?.node}
                        content={excerpt}
                        date={date}
                        title={title}
                        isInnerLink={true}
                        linkText={btnText}
                        isNews={true}
                        linkUrl={removeLastTrailingSlash(uri)}
                      />
                    </li>
                  );
                })}
              </ul>
            )}
            {totalPages > 1 && (
              <Pagination
                currentPage={queryPage}
                totalPages={totalPages}
                className="mt-15 md:mt-25"
              />
            )}
          </Section>
        </>
      )}
    </>
  );
};

export default withLayout(Page);

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  const client = getApolloClient();

  const location =
    locale == 'uk'
      ? MenuLocationEnum.PRIMARY_NAVIGATION
      : MenuLocationEnum.PRIMARY_NAVIGATION___EN;
  const language = locale?.toUpperCase();
  const newsLanguage = language;
  const { data } = await client.query({
    query: NEWS_PAGE_QUERY,
    variables: {
      language,
      newsLanguage,
    },
  });

  const {
    data: { menuItems },
  } = await client.query({
    query: ALL_MENU_QUERY,
    variables: {
      location,
    },
  });
  const footerLocation =
    locale == 'uk'
      ? FooterMenuLocationEnum.FOOTER_MENU
      : FooterMenuLocationEnum.FOOTER_MENU___EN;

  const { data: footerMenuItems } =
    locale == 'uk'
      ? await client.query({
          query: FOOTER_DATA_QUERY,
          variables: {
            location: footerLocation,
          },
        })
      : await client.query({
          query: FOOTER_DATA_QUERY_EN,
          variables: {
            location: footerLocation,
          },
        });
  const footerData = footerDataTransformer(footerMenuItems);

  const flatList = flatListToHierarchical(menuItems.nodes);
  const menus = transformMenus(flatList);

  return {
    props: {
      data,
      menus,
      footerData,
      ...(await serverSideTranslations(locale as string, ['common'])),
    },
  };
};
